import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import {getManufacturerByPublicId } from "../../api/Manufacturer";
import ManufacturerForm from "../../components/manufacturer/ManufacturerForm";
import ManufacturerErrorCodesTable from '../../components/manufacturer/ManufacturerErrorCodesForm';
import LoadingSpinner from "../../components/LoadingSpinner";

import { updateErrorCode, deleteManufacturerErrorCode, getManufacturerErrorCodes, createErrorCode } from "../../api/Manufacturer";


import toast from "react-hot-toast";
import LoadingTablePage from "../LoadingTablePage";

import ConfirmationModal from "../../components/ConfirmationModal";

const EditManufacturerPage = () => {
    const { t } = useTranslation();
    const { getAccessToken } = useAuth();


    const location = useLocation();
    const query = new URLSearchParams(location.search);

    const initialTab = query.get("tab") || "details";
    const [activeTab, setActiveTab] = useState(initialTab);
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const loadedRef = useRef(false);

    const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
    const [originalManufacturer, setOriginalManufacturer] = useState(null);
    const [manufacturer, setManufacturer] = useState({
        manufacturerPublicId: "",
        manufacturerName: "",
    });
    const [errorCodes, setErrorCodes] = useState([]);
    const [deleteState, setDeleteState] = useState({
        isOpen: false,
        row: null,
    });

    const [savingRowId, setSavingRowId] = useState(null);

    const saveErrorCode = async (row) => {
        if (!manufacturer?.manufacturerPublicId) return;

        if (!row.errorCode?.trim()) {
            toast.error(t("manufacturer.errorCodeRequired"));
            return;
        }

        row.manufacturerPublicId = manufacturer.manufacturerPublicId;
        const token = await getAccessToken();
        setSavingRowId(row.errorPublicId);

        try {
            const isCreate = row.isNew || row.errorPublicId?.startsWith("temp-");

            if (isCreate) {
                await createErrorCode(row, token);
            } else {
                await updateErrorCode(row, token);
            }

            const list = await getManufacturerErrorCodes(manufacturer.manufacturerPublicId, token);
            setErrorCodes(list);

            toast.success(t("toast.saved"));
        } catch (err) {
            console.error(err);
            toast.error(t("toast.failedSave"));
        } finally {
            setSavingRowId(null);
        }
    };


    const updateErrorCodes = (updatedRow) => {
        if (updatedRow.__forceAddToTop) {
            const cleanRow = { ...updatedRow };
            delete cleanRow.__forceAddToTop;
            cleanRow.isNew = true;

            setErrorCodes((prev) => [cleanRow, ...prev]);
            return;
        }

        if (updatedRow.__cancelAdd) {
            setErrorCodes((prev) => prev.filter((x) => x.errorPublicId !== updatedRow.errorPublicId));
            return;
        }

        setErrorCodes((prev) =>
            prev.map((x) => (x.errorPublicId === updatedRow.errorPublicId ? updatedRow : x))
        );
    };



    const handleInputChange = (key, value) => {
        const updatedManufacturer = { ...manufacturer, [key]: value };
        setManufacturer(updatedManufacturer);

        const isChanged =
            JSON.stringify(updatedManufacturer) !==
            JSON.stringify(originalManufacturer);
        setHasChanges(isChanged);
    };

    useEffect(() => {
        const query = new URLSearchParams(location.search);
        query.set("tab", activeTab);
        navigate(`${location.pathname}?${query.toString()}`, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [activeTab, location.pathname, navigate]);

    useEffect(() => {
        if (loadedRef.current) return;
        loadedRef.current = true;

        handleGetManufacturer();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [id]);

    const handleGetManufacturer = async () => {
        try {
            const accessToken = await getAccessToken();
            const m = await getManufacturerByPublicId(accessToken, id);
            
            setOriginalManufacturer(m);
            setManufacturer(m);
            setHasChanges(false);

        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        async function load() {
            if (!manufacturer?.manufacturerPublicId) return;

            setLoading(true); 

            try {
                const token = await getAccessToken();
                const list = await getManufacturerErrorCodes(
                    manufacturer.manufacturerPublicId,
                    token
                );
                setErrorCodes(list);
            } catch (err) {
                console.error("Failed to load error codes", err);
            } finally {
                setLoading(false); 
            }
        }

        if (activeTab === "errorCodes") {
            load();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    }, [activeTab, manufacturer?.manufacturerPublicId]);



    const confirmDelete = async () => {
        const row = deleteState.row;
        if (!row) return;

        // --- Temp-row: remove locally ---
        if (row.errorPublicId.startsWith("temp-")) {
            setErrorCodes(prev =>
                prev.filter(x => x.errorPublicId !== row.errorPublicId)
            );


            setDeleteState({ isOpen: false, row: null });
            toast.success(t("manufacturer.errorCodeRemoved"));
            return;
        }

        // --- Real row: delete from API ---
        try {
            const token = await getAccessToken();
            await deleteManufacturerErrorCode(row.errorPublicId, token);

            setErrorCodes(prev =>
                prev.filter(x => x.errorPublicId !== row.errorPublicId)
            );


            toast.success(t("manufacturer.errorCodeDeleted"));

        } catch (err) {
            toast.error(t("manufacturer.failedDeleteErrorCode"));
        }

        setDeleteState({ isOpen: false, row: null });
    };



    const handleBackClick = () => {
        const go = () => {
            if (manufacturer?.manufacturerPublicId) {
                navigate(`/manufacturers`);
            } else if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate("/manufacturers");
            }
        };

        if (hasChanges) {
            setConfirmState((s) => ({
                ...s,
                isOpen: true,
                title: t("confirm.discard"),
                confirm: () => {
                    setConfirmState((p) => ({ ...p, isOpen: false }));
                    go();
                },
            }));
        } else {
            go();
        }
    };
    if (loading) {
        return (
            <>
                <HeaderBar />
                <div className="flex flex-row w-full">
                    <Sidebar />

                    <main className="page flex items-center justify-center">
                        <LoadingSpinner />
                    </main>
                </div>
            </>
        );
    }

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("pages.editManufacturer")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button
                                onClick={() => handleBackClick()}
                                className="text-black flex"
                            >
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>
                            <h2 className="border-l border-mute-200 pl-4 ">
                                {manufacturer?.manufacturerName}
                            </h2>
                            <div className="flex gap-4 ml-auto">
                                <button
                                    className="btn btn-outline-primary"
                                    onClick={() => handleBackClick()}
                                >
                                    {t("common.cancel")}
                                </button>
                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges}
                                  //  onClick={handleSaveAll}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className=" px-4 py-8 shadow-lg rounded-md w-full">

                            <div className="border-b border-mute-200 mb-4 gap-4 flex">
                                <button
                                    className={`px-4 pb-2 ${activeTab === "details"
                                            ? "border-b-2 border-black font-bold text-sm text-black"
                                            : "text-mute-400 text-sm font-bold"
                                        }`}
                                    onClick={() => setActiveTab("details")}
                                >
                                    {t("tabs.details")}
                                </button>
                                <button
                                    className={`px-4 pb-2 ${activeTab === "errorCodes"
                                        ? "border-b-2 border-black font-bold text-sm text-black"
                                        : "text-mute-400 text-sm font-bold"
                                        }`}
                                    onClick={() => setActiveTab("errorCodes")}
                                >
                                    {t("tabs.errorCodes")}
                                </button>
                            </div>

                            <div className="flex flex-col w-full gap-4">
                                {activeTab === "details" && (
                                    <ManufacturerForm
                                        manufacturer={manufacturer}
                                        handleInputChange={handleInputChange}
                                    />
                                )}

                                {activeTab === "errorCodes" && manufacturer?.manufacturerPublicId && (
                                    <ManufacturerErrorCodesTable
                                        errorCodes={errorCodes}
                                        onChange={updateErrorCodes}
                                        onDelete={(row) => setDeleteState({ isOpen: true, row })}
                                        onSave={saveErrorCode}
                                        savingRowId={savingRowId}
                                    />
                                )}

                            </div>
                        </div>
                    </div>
                </main>
                <ConfirmationModal
                    isOpen={confirmState.isOpen}
                    title={confirmState.title}
                    onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
                    onConfirm={() => {
                        setConfirmState({ ...confirmState, isOpen: false });
                        confirmState.confirm();
                    }}
                />
                <ConfirmationModal
                    isOpen={deleteState.isOpen}
                    title={t("manufacturer.deleteErrorCode")}
                    onClose={() => setDeleteState({ isOpen: false, row: null })}
                    onConfirm={confirmDelete}
                />

            </div>
        </>
    );
};

export default EditManufacturerPage;
